// components/entities/ScoredAnalysis.tsx
//
// THE scored-analysis panel of a shortlist row. A PortfolioFit, a Lead and a UnitFit are judged by
// the same kind of run against the same kind of rubric, so they get the same panel: one verdict
// table, one detailed table behind "See more", one column menu.
//
// Everything here HIDES what has no data instead of rendering an empty shell:
//   - the whole panel is omitted by the caller when there is neither a verdict nor a run in flight
//     (BrandShortlistDetail then drops its Scoring tab entirely),
//   - "See more" only appears when there IS evidence to see, so a row scored before
//     prisma/manual/2026-08-01_unit_fit_evidence.sql does not offer a detailed view of nothing,
//   - the detailed table only draws the columns that carry data (analysisColumnsWithData).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Clock3, Database, Eye, ExternalLink, Loader2, SlidersHorizontal } from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import type { AnalysisColumnId, AnalysisViewMode, CriterionGroup } from '#types/types';
import {
  analysisColumnsWithData,
  filterCriterionGroups,
  hasAnalysisEvidence,
} from '#lib/leasing/analysisGroups';
import { domainFromUrl } from '#lib/web/favicon';
import { useKernelStore } from '@/kernel/kernelStores';
import tableStyles from '@/components/portal/Table.module.css';
import { TABLE_GLASS_HEADER } from '@/components/portal/tableSurface';
import { InventorySearch } from '@/components/portal/InventoryUI';
import { ScoreGroup, ScoreRow } from '@/components/ui/ScoreRows';

/** What the run is doing while it has nothing to show yet. `null` once it has. */
export type ScoredAnalysisRunState = 'running' | 'pending' | 'aborted' | null;

interface ScoredAnalysisProps {
  groups: CriterionGroup[];
  runState: ScoredAnalysisRunState;
  viewMode: AnalysisViewMode;
  onViewModeChange: (mode: AnalysisViewMode) => void;
}

const TRANSLATIONS = {
  en: {
    initializing: 'Initializing research...',
    waitingInQueue: 'Waiting in queue (not picked yet)...',
    analysisInterrupted: 'Analysis interrupted (campaign stopped).',
    colScore: 'Score',
    notScored: 'Not scored',
    colCriterion: 'Criteria',
    colFacts: 'Facts',
    colLinks: 'Links',
    colInquiries: 'Inquiries',
    colReasoning: 'Why',
    columns: 'Columns',
    searchPlaceholder: 'Search analysis...',
    noAnalysis: 'No analysis yet.',
    seeMore: 'See more',
    seeLess: 'See less',
  },
  fr: {
    initializing: 'Initialisation de la recherche...',
    waitingInQueue: 'En attente (pas encore pris par le worker)...',
    analysisInterrupted: 'Analyse interrompue (campagne arretee).',
    colScore: 'Note',
    notScored: 'Non notée',
    colCriterion: 'Critères',
    colFacts: 'Faits',
    colLinks: 'Sources',
    colInquiries: 'Interrogations',
    colReasoning: 'Pourquoi',
    columns: 'Colonnes',
    searchPlaceholder: 'Rechercher dans l analyse...',
    noAnalysis: 'Aucune analyse disponible.',
    seeMore: 'Voir plus',
    seeLess: 'Voir moins',
  },
} as const;

const DEFAULT_COLUMN_WIDTHS: Record<AnalysisColumnId, number> = {
  score: 70,
  criterion: 200,
  inquiries: 260,
  facts: 320,
  links: 150,
};

const ratioColor = (ratio: number) => {
  if (ratio > 0.7) return 'text-[#4DAB9A]';
  if (ratio > 0.4) return 'text-[#FFA344]';
  return 'text-[#979A9B]';
};

const scoreClassName = (group: CriterionGroup) =>
  ratioColor(
    typeof group.score === 'number' && typeof group.maxScore === 'number' && group.maxScore > 0
      ? group.score / group.maxScore
      : 0
  );

/**
 * Writes the column template straight onto the table node. A resize must not re-render the whole
 * analysis on every mouse move, so the dragged widths live in a ref and only ever reach the DOM
 * through here.
 */
const applyColumnTemplate = (
  node: HTMLDivElement | null,
  columns: string[],
  widths: Record<string, number>
) => {
  if (!node) return;
  node.style.setProperty('--table-grid-cols', columns.map((column) => `${widths[column] ?? 120}fr`).join(' '));
};

// ChatGPT's secondary pill and card (owner, 2026-09-28), the Source right bar's PANEL_BUTTON skin.
const toggleButtonClassName =
  'inline-flex h-9 items-center gap-2 rounded-full border border-[#262626] bg-[#1B1B1B] px-4 text-[#E5E5E5] transition-colors hover:bg-[#2A2A2A] hover:text-white';
const TOGGLE_STYLE = { fontSize: 13 } as const;
// No overflow-hidden: the container's own overflow-x:auto already clips to the radius and keeps the detail view scrollable.
const CARD_TABLE = '!rounded-[20px] !border-[#262626] !bg-[#1B1B1B]';

export const ScoredAnalysis = ({ groups, runState, viewMode, onViewModeChange }: ScoredAnalysisProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = TRANSLATIONS[lang] ?? TRANSLATIONS.en;

  const [search, setSearch] = useState('');
  const [hiddenColumns, setHiddenColumns] = useState<Set<AnalysisColumnId>>(() => new Set());
  const [columnsMenuOpen, setColumnsMenuOpen] = useState(false);
  const tableRef = useRef<HTMLDivElement>(null);
  const columnWidthsRef = useRef<Record<string, number>>({ ...DEFAULT_COLUMN_WIDTHS });

  const columnLabels = useMemo<Record<AnalysisColumnId, string>>(
    () => ({
      score: t.colScore,
      criterion: t.colCriterion,
      inquiries: t.colInquiries,
      facts: t.colFacts,
      links: t.colLinks,
    }),
    [t]
  );

  const hasEvidence = useMemo(() => hasAnalysisEvidence(groups), [groups]);
  const populatedColumns = useMemo(() => analysisColumnsWithData(groups), [groups]);
  const visibleColumns = useMemo(
    () => populatedColumns.filter((column) => !hiddenColumns.has(column)),
    [populatedColumns, hiddenColumns]
  );
  const renderableGroups = useMemo(() => filterCriterionGroups(groups, search), [groups, search]);

  // Rows with nothing but a name have nothing to say in the verdict table; their evidence, if any,
  // lives in the detailed view.
  const verdictRows = useMemo(
    () => groups.filter((group) => typeof group.score === 'number' || (group.reasoning ?? '').trim().length > 0),
    [groups]
  );

  /** The column set as one value, so the effects below never depend on a function or an array. */
  const visibleColumnsKey = visibleColumns.join('|');

  const startColumnDrag = useCallback(
    (column: AnalysisColumnId, startX: number) => {
      const startWidth = columnWidthsRef.current[column] ?? 120;
      const onMove = (e: MouseEvent) => {
        columnWidthsRef.current = {
          ...columnWidthsRef.current,
          [column]: Math.max(60, startWidth + (e.clientX - startX)),
        };
        applyColumnTemplate(tableRef.current, visibleColumnsKey.split('|'), columnWidthsRef.current);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [visibleColumnsKey]
  );

  const autoSizeColumn = useCallback(
    (column: AnalysisColumnId) => {
      if (!tableRef.current) return;
      const cells = tableRef.current.querySelectorAll<HTMLElement>(`[data-col-id="${column}"]`);
      let max = 80;
      cells.forEach((cell) => {
        const inner = cell.firstElementChild as HTMLElement | null;
        const current = (inner ?? cell).scrollWidth + 32;
        if (current > max) max = current;
      });
      columnWidthsRef.current = { ...columnWidthsRef.current, [column]: Math.min(max, 800) };
      applyColumnTemplate(tableRef.current, visibleColumnsKey.split('|'), columnWidthsRef.current);
    },
    [visibleColumnsKey]
  );

  const showDetailed = viewMode === 'detailed' && hasEvidence;

  // Rendering restores the default template, so put the resized widths back whenever the column set
  // changes or the detailed table mounts again.
  useEffect(() => {
    applyColumnTemplate(tableRef.current, visibleColumnsKey.split('|'), columnWidthsRef.current);
  }, [visibleColumnsKey, showDetailed]);

  useEffect(() => {
    if (!columnsMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-cols-menu-root]')) return;
      setColumnsMenuOpen(false);
    };
    const timer = window.setTimeout(() => document.addEventListener('mousedown', onDown), 50);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onDown);
    };
  }, [columnsMenuOpen]);

  const toggleColumn = useCallback((column: AnalysisColumnId) => {
    setHiddenColumns((previous) => {
      const next = new Set(previous);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return next;
    });
  }, []);

  const showSimple = useCallback(() => onViewModeChange('simple'), [onViewModeChange]);
  const showDetail = useCallback(() => onViewModeChange('detailed'), [onViewModeChange]);
  const openColumnsMenu = useCallback(() => setColumnsMenuOpen((open) => !open), []);

  if (groups.length === 0 && !runState) return null;

  const ticker = groups.length > 0 || !runState ? null : (
    <div className="mb-2 flex items-center gap-2 text-[10px] text-white/40">
      {runState === 'running' ? <Loader2 size={10} className="animate-spin" /> : <Clock3 size={10} />}
      <span>
        {runState === 'running'
          ? t.initializing
          : runState === 'aborted'
            ? t.analysisInterrupted
            : t.waitingInQueue}
      </span>
    </div>
  );

  if (!showDetailed) {
    return (
      <div className="py-4">
        {ticker}
        {verdictRows.length > 0 && (
          // The same rows as the Fater score in Data, so a score reads one way everywhere.
          <ScoreGroup
            // No subtotal: the raw points do not add up to the normalised fit score on the row above.
            title={t.colCriterion}
            action={hasEvidence ? (
              <button type="button" onClick={showDetail} className="-mr-3 inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[#AFAFAF] transition-colors hover:bg-white/[0.06] hover:text-white" style={{ fontSize: 13 }}>
                <Eye size={14} strokeWidth={2} />
                {t.seeMore}
              </button>
            ) : null}
          >
            {verdictRows.map((group) => (
              <ScoreRow key={group.id} label={group.label} earned={typeof group.score === 'number' ? group.score : null}
                maximum={typeof group.maxScore === 'number' ? group.maxScore : null} reason={group.reasoning} missingLabel={t.notScored} />
            ))}
          </ScoreGroup>
        )}
      </div>
    );
  }

  // Rendered from the DEFAULTS, never from the widths ref: a drag owns the node's own
  // --table-grid-cols and the effect above puts it back after a re-render.
  const template = visibleColumns.map((column) => `${DEFAULT_COLUMN_WIDTHS[column]}fr`).join(' ');
  const minWidth = visibleColumns.reduce((total, column) => total + DEFAULT_COLUMN_WIDTHS[column], 0);

  return (
    <div className="py-4">
      {ticker}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <InventorySearch value={search} onChange={setSearch} placeholder={t.searchPlaceholder} />
          <div className="relative" data-cols-menu-root>
            <button
              type="button"
              onClick={openColumnsMenu}
              className={toggleButtonClassName}
              title={t.columns}
            >
              <SlidersHorizontal size={16} strokeWidth={2.1} />
              <span className="hidden sm:inline">{t.columns}</span>
            </button>
            {columnsMenuOpen && (
              <div className="absolute left-0 top-full z-50 mt-2 w-[220px] overflow-hidden rounded-[16px] border border-white/[0.06] bg-[#353535] p-2 shadow-[0_20px_55px_rgba(0,0,0,0.56)]">
                <div className="px-2 pb-1 pt-0.5 text-[11px] text-white/45">{t.columns}</div>
                {populatedColumns.map((column) => (
                  <ColumnToggle
                    key={column}
                    column={column}
                    label={columnLabels[column]}
                    visible={!hiddenColumns.has(column)}
                    onToggle={toggleColumn}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <button type="button" onClick={showSimple} className={toggleButtonClassName} style={TOGGLE_STYLE}>
          {t.seeLess}
        </button>
      </div>

      <div
        ref={tableRef}
        className={clsx(tableStyles.tableContainer, CARD_TABLE)}
        style={{ '--table-grid-cols': template, '--table-min-width': `${minWidth}px` } as React.CSSProperties}
      >
        <div className={clsx(tableStyles.gridRow, tableStyles.headerRow)}>
          {visibleColumns.map((column, index) => (
            <div key={column} className={clsx(tableStyles.cell, 'relative overflow-hidden')}>
              <span className="truncate">{columnLabels[column]}</span>
              {index < visibleColumns.length - 1 && (
                <ColumnResizer column={column} onDrag={startColumnDrag} onAutoSize={autoSizeColumn} />
              )}
            </div>
          ))}
        </div>

        {renderableGroups.length === 0 ? (
          <div className="p-6 text-center text-[13px] text-[#8E8E8E]">{t.noAnalysis}</div>
        ) : (
          <div
            className="grid w-full"
            style={{ gridTemplateColumns: 'var(--table-grid-cols)', minWidth: `${minWidth}px` }}
          >
            {renderableGroups.flatMap((group) => {
              const rows = group.buckets.reduce((total, bucket) => total + Math.max(bucket.facts.length, 1), 0);
              const cells: React.ReactNode[] = [];

              if (visibleColumns.includes('score')) {
                cells.push(
                  <div
                    key={`${group.id}-score`}
                    data-col-id="score"
                    className="flex items-center justify-center overflow-hidden border-b border-r border-[#262626] bg-[#1B1B1B] px-3 py-2"
                    style={{ gridRow: `span ${rows}` }}
                  >
                    {typeof group.score === 'number' && typeof group.maxScore === 'number' ? (
                      <span className={clsx('font-mono font-semibold', scoreClassName(group))} style={{ fontSize: 13 }}>
                        {group.score}/{group.maxScore}
                      </span>
                    ) : null}
                  </div>
                );
              }

              if (visibleColumns.includes('criterion')) {
                cells.push(
                  <div
                    key={`${group.id}-criterion`}
                    data-col-id="criterion"
                    className="flex items-center overflow-hidden border-b border-r border-[#262626] bg-[#1B1B1B] px-4 py-2"
                    style={{ gridRow: `span ${rows}` }}
                  >
                    <span
                      className={clsx('min-w-0 text-[12px] font-semibold text-white/85', rows > 1 ? 'break-words' : 'truncate')}
                      title={group.labelFull}
                    >
                      {group.label}
                    </span>
                  </div>
                );
              }

              group.buckets.forEach((bucket, bucketIndex) => {
                const rowsInBucket = Math.max(bucket.facts.length, 1);

                if (visibleColumns.includes('inquiries')) {
                  cells.push(
                    <div
                      key={`${group.id}-inquiry-${bucketIndex}`}
                      data-col-id="inquiries"
                      className="flex items-center overflow-hidden border-b border-r border-[#262626] bg-[#1B1B1B] px-4 py-2"
                      style={{ gridRow: `span ${rowsInBucket}` }}
                    >
                      {typeof bucket.inquiry?.question === 'string' ? (
                        <span
                          className={clsx('min-w-0 text-[11px] text-white/75', rowsInBucket > 1 ? 'break-words' : 'truncate')}
                          title={bucket.inquiry.question}
                        >
                          {bucket.inquiry.question}
                        </span>
                      ) : null}
                    </div>
                  );
                }

                for (let row = 0; row < rowsInBucket; row++) {
                  const fact = bucket.facts[row];

                  if (visibleColumns.includes('facts')) {
                    cells.push(
                      <div
                        key={`${group.id}-fact-${bucketIndex}-${row}`}
                        data-col-id="facts"
                        className="flex items-center overflow-hidden border-b border-r border-[#262626] px-4 py-2"
                      >
                        {fact ? (
                          <div className="flex min-w-0 items-start gap-1.5">
                            {fact.isInternal && <Database size={10} className="mt-0.5 shrink-0 text-indigo-400" />}
                            <span
                              className={clsx(
                                'whitespace-normal break-words text-[12px] leading-snug',
                                fact.isInternal ? 'text-indigo-200/80' : 'text-white/80'
                              )}
                            >
                              {fact.claim}
                              {fact.evidenceCheck && (
                                <span className="ml-1 text-[10px] text-[#8A8A8A]">
                                  ({fact.evidenceCheck.status === 'supported' ? 'Source supports claim' : fact.evidenceCheck.status === 'contradicted' ? 'Source contradicts claim' : 'Source support unresolved'})
                                </span>
                              )}
                            </span>
                          </div>
                        ) : row === 0 && bucketIndex === 0 && group.facts.length === 0 && group.reasoning ? (
                          // No recorded facts for this criterion: fall back to the Judge's
                          // reasoning so the row is never an empty shell.
                          <span className="whitespace-normal break-words text-[12px] italic leading-snug text-white/60">
                            {group.reasoning}
                          </span>
                        ) : null}
                      </div>
                    );
                  }

                  if (visibleColumns.includes('links')) {
                    cells.push(
                      <div
                        key={`${group.id}-link-${bucketIndex}-${row}`}
                        data-col-id="links"
                        className="flex items-center overflow-hidden border-b border-[#262626] px-4 py-2"
                      >
                        {fact?.isInternal ? (
                          <span
                            className="truncate font-mono text-[10px] text-indigo-400/70"
                            title={fact.exactQuote || 'Fater Intelligence'}
                          >
                            {fact.exactQuote || 'Fater Intelligence'}
                          </span>
                        ) : fact?.sourceUrl ? (
                          <a
                            href={fact.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex min-w-0 items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 hover:underline"
                            title={fact.sourceUrl}
                          >
                            <ExternalLink size={10} className="shrink-0" />
                            <span className="truncate">{domainFromUrl(fact.sourceUrl)}</span>
                          </a>
                        ) : null}
                      </div>
                    );
                  }
                }
              });

              return cells;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ColumnToggle = React.memo(
  ({
    column,
    label,
    visible,
    onToggle,
  }: {
    column: AnalysisColumnId;
    label: string;
    visible: boolean;
    onToggle: (column: AnalysisColumnId) => void;
  }) => (
    <button
      type="button"
      onClick={() => onToggle(column)}
      className="flex h-9 w-full items-center gap-2 rounded-[10px] px-2 text-left text-white/90 transition-colors hover:bg-[#232323]"
    >
      <span
        className={clsx(
          'inline-flex h-4 w-4 items-center justify-center rounded border',
          visible ? 'border-white/30 bg-white/10 text-white/90' : 'border-white/15 bg-transparent text-transparent'
        )}
      >
        {visible ? '✓' : ''}
      </span>
      <span className="text-[13px]">{label}</span>
    </button>
  )
);
ColumnToggle.displayName = 'ColumnToggle';

const ColumnResizer = React.memo(
  ({
    column,
    onDrag,
    onAutoSize,
  }: {
    column: AnalysisColumnId;
    onDrag: (column: AnalysisColumnId, startX: number) => void;
    onAutoSize: (column: AnalysisColumnId) => void;
  }) => (
    <div
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize. Double-click to auto-size."
      className="absolute right-[-3px] top-0 z-20 h-full w-1.5 cursor-col-resize hover:bg-white/10"
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDrag(column, e.clientX);
      }}
      onDoubleClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onAutoSize(column);
      }}
    />
  )
);
ColumnResizer.displayName = 'ColumnResizer';
